import type { FieldType } from '@/api/Fields/types.ts'
import type { Platform } from '@/api/Link/types.ts'
import type { FetchParams, Paginated } from '@/api/types.ts'

export enum InitiativeRole {
  ADMIN = 'ADMIN',
  DASHBOARD = 'DASHBOARD',
}

export enum InitiativeAccessLevel {
  FULL = 'full',
  DASHBOARD_ONLY = 'dashboard_only',
}

export interface InitiativeData {
  id: number
  accessLevel?: InitiativeAccessLevel
  name: string
  businessName?: string
  businessLogoURL?: string
  features: { id: number; name: string }
  featuresCount?: number | null
  recentCollectsCount?: number | null
  lastActivityAt?: string | null
}

export interface InitiativeFetchParams extends FetchParams {
  businessId?: number | number[]
}

export interface InitiativeFullData {
  id: number
  name: string
  description: string
  businessId?: number | string
  reportLink?: string
  responsibleName?: string
  responsiblePhone?: string
  responsibleEmail?: string
}

export type InitiativeFormData = Omit<InitiativeFullData, 'id'>

export interface InternalFeature {
  featureId: number
  name: string
}

export interface InternalFeaturesPayload {
  featureIds: number[]
}

export interface InitiativeFullAccessDetail extends InitiativeFullData {
  accessLevel: InitiativeAccessLevel.FULL
  businessName?: string
  internalFeatures: InternalFeature[]
}

export interface InitiativeDashboardDetail {
  id: number
  name: string
  businessId: number
  businessName: string
  businessLogoURL?: string
  reportLink?: string
  accessLevel: InitiativeAccessLevel.DASHBOARD_ONLY
  internalFeatures: InternalFeature[]
}

export type InitiativeDetail =
  | InitiativeFullAccessDetail
  | InitiativeDashboardDetail

export type InitiativePermissionOrderBy =
  | 'name'
  | 'email'
  | 'role'
  | 'createdAt'

export interface InitiativePermissionsParams extends FetchParams {
  role?: InitiativeRole
  orderBy?: InitiativePermissionOrderBy
}

export interface InitiativePermissionItem {
  userId: number
  name: string
  email: string
  role: InitiativeRole
  createdAt: string
}

export interface InitiativePermissionCounts {
  all: number
  admin: number
  dashboard: number
}

export interface InitiativePermissionsResult extends Paginated<InitiativePermissionItem> {
  counts: InitiativePermissionCounts
}

export interface InitiativeAddPermissionsPayload {
  userIds: number[]
  role: InitiativeRole
}

export interface InitiativeChangePermissionRolePayload {
  role: InitiativeRole
}

export interface InitiativeSearchParams {
  search: string
  platform: Platform
}

export interface InitiativeSearchResult {
  collectId: number
  featureId: number
  featureName: string
  fieldName: string
  fieldValue: string
}

export interface InitiativeSummary {
  totalCollects: number
  totalCollectsMonthVariation: number
  todayCollects: number
  lastCollectAt: string | null
  last7DaysCollects: number
  last7DaysWeekVariation: number
  totalFiles: number
  activeCollectors: number
}

export interface InitiativeLastCollect {
  id: number
  featureId: number
  featureName: string
  userName?: string
  collectDateTime: string
}

export interface InitiativeSearchCollectsParams extends FetchParams {
  search: string
  limit: number
  featureId?: number
  projections?: string[]
}

export interface InitiativeSearchCollectField {
  alias: string
  name: string
  type: FieldType
  value: InitiativeSearchCollectFieldValue
}

export type InitiativeSearchCollectFieldValue =
  | string
  | number
  | boolean
  | string[]
  | null

export interface InitiativeSearchCollect {
  id: number
  featureId: number
  featureName: string
  userName?: string
  collectDateTime: string
  platform: Platform[]
  data: InitiativeSearchCollectField[]
}

export interface InitiativeSearchFeature {
  id: number
  name: string
  registers: number
}

export interface InitiativeSearchCollectsResult extends Paginated<InitiativeSearchCollect> {
  page: number
  pages: number
  features: InitiativeSearchFeature[]
}
